import { useState } from 'react';
import { FormFields } from './useForm';

export interface FieldLabel { shrink: boolean }
export type FormLabels<T extends string> = Record<T, FieldLabel>;

export interface UseLabelShrinkResult<T extends string> {
  labels: FormLabels<T>;
  setLabelShrink: (fieldName: T, value: boolean) => void
}

// Since MUI input will move label to top if there is some animation
// we'll use this hook to explicitly tell MUI input what to do with label
export const useLabelShrink = <T extends string>(
  fields: FormFields<T>
): UseLabelShrinkResult<keyof typeof fields> => {
  const getInitialLabels = (fields: FormFields<T>): FormLabels<T> => {
    const fieldKeys = Object.keys(fields) as T[];

    const initialState: FormLabels<T> = fieldKeys.reduce(
      (state: FormLabels<T>, fieldKey: T) => {
        const { name: fieldName } = fields[fieldKey];

        return ({
          ...state,
          [fieldName]: { shrink: false }
        });
      }, {} as FormLabels<T>
    );

    return initialState;
  };

  const [ labels, setLabels ] = useState<FormLabels<T>>(() => getInitialLabels(fields));

  const setLabelShrink = (
    fieldName: T, value: boolean
  ): void => {
    setLabels(prevLabels => ({
      ...prevLabels,
      [fieldName]: { shrink: value }
    }));
  };

  return { labels, setLabelShrink };
};
